import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth';
import { useMultilingual } from '../hooks/useMultilingual';

const looksLikeEmail = (value: string) => value.includes('@');

const looksLikePhone = (value: string) => {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 8 && digits.length <= 15;
};

const Login: React.FC = () => {
  const { getText, language, setLanguage, isRTL } = useMultilingual();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [identifierError, setIdentifierError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const navigate = useNavigate();

  const validateIdentifier = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) {
      return getText('Email or phone is required', 'البريد الإلكتروني أو رقم الهاتف مطلوب');
    }

    if (looksLikeEmail(trimmed)) {
      const emailRegex =
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      if (!emailRegex.test(trimmed)) {
        return getText('Please enter a valid email address', 'يرجى إدخال بريد إلكتروني صالح');
      }
      if (trimmed.length > 254) {
        return getText('Email address is too long', 'البريد الإلكتروني طويل جداً');
      }
      return '';
    }

    if (!looksLikePhone(trimmed)) {
      return getText(
        'Enter a valid email or phone number',
        'أدخل بريداً إلكترونياً أو رقم هاتف صالحاً'
      );
    }

    return '';
  };

  const buildLoginPayload = (id: string, pwd: string) => {
    const trimmed = id.trim();
    if (looksLikeEmail(trimmed)) {
      return { email: trimmed, password: pwd };
    }
    return { phone: trimmed, password: pwd };
  };

  const localizeError = (message: string): string => {
    const lower = message.toLowerCase();
    if (lower.includes('verify your email') || lower.includes('verification')) {
      return getText(
        'Your account is not verified. Please check your phone or email for the verification code.',
        'حسابك غير مفعّل. يرجى التحقق من هاتفك أو بريدك للحصول على رمز التفعيل.'
      );
    }
    if (lower.includes('invalid') || lower.includes('credentials') || lower.includes('unauthorized')) {
      return getText(
        'Invalid email/phone or password. Please try again.',
        'البريد/الهاتف أو كلمة المرور غير صحيحة. حاول مرة أخرى.'
      );
    }
    if (lower.includes('login failed')) {
      return getText('Sign-in failed. Please try again.', 'فشل تسجيل الدخول. حاول مرة أخرى.');
    }
    return message;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextIdError = validateIdentifier(identifier);
    setIdentifierError(nextIdError);
    if (nextIdError || !password.trim()) {
      if (!password.trim()) {
        setError(getText('Password is required', 'كلمة المرور مطلوبة'));
      }
      return;
    }

    setLoading(true);
    setError('');
    setShowEmailVerification(false);
    setResendSuccess(false);

    try {
      await authService.login(buildLoginPayload(identifier, password));
      navigate('/dashboard');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : getText('Sign-in failed', 'فشل تسجيل الدخول');
      setError(localizeError(errorMessage));

      if (
        errorMessage.toLowerCase().includes('verify') ||
        errorMessage.toLowerCase().includes('verification')
      ) {
        setShowEmailVerification(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    setResendSuccess(false);

    try {
      const trimmed = identifier.trim();
      await authService.sendOtp(
        looksLikeEmail(trimmed) ? { email: trimmed } : { phone: trimmed }
      );
      setResendSuccess(true);
      setError('');
    } catch (err) {
      setError(
        localizeError(
          err instanceof Error
            ? err.message
            : getText('Failed to resend verification code', 'فشل إعادة إرسال رمز التفعيل')
        )
      );
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Box
      dir={isRTL ? 'rtl' : 'ltr'}
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 4,
        background: 'linear-gradient(160deg, #1a1918 0%, #2c2a28 45%, #3d2a1f 100%)',
      }}
    >
      <Container maxWidth="sm" disableGutters>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={language}
            onChange={(_e, value: 'en' | 'ar' | null) => {
              if (value) setLanguage(value);
            }}
            sx={{
              bgcolor: 'rgba(255,255,255,0.08)',
              borderRadius: 2,
              '& .MuiToggleButton-root': {
                color: 'rgba(255,255,255,0.75)',
                border: 'none',
                px: 1.5,
                '&.Mui-selected': {
                  bgcolor: '#FF6500',
                  color: '#fff',
                  '&:hover': { bgcolor: '#e55a00' },
                },
              },
            }}
          >
            <ToggleButton value="ar">العربية</ToggleButton>
            <ToggleButton value="en">English</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            width: '100%',
            maxWidth: 440,
            mx: 'auto',
            borderRadius: 3,
            border: '1px solid rgba(255,101,0,0.18)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                mx: 'auto',
                mb: 2,
                backgroundColor: '#21201F',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 1.25,
              }}
            >
              <img
                src="/logo192.png"
                alt="EDL Autoparts"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </Box>
            <Typography
              variant="h5"
              component="h1"
              sx={{ color: '#FF6500', fontWeight: 700, mb: 0.75 }}
            >
              {getText('EDL Auto Parts', 'إي دي إل لقطع الغيار')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {getText('Sign in to your dashboard', 'سجّل الدخول إلى لوحة التحكم')}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2, textAlign: isRTL ? 'right' : 'left' }}>
              {error}
            </Alert>
          )}

          {resendSuccess && (
            <Alert severity="success" sx={{ mb: 2, textAlign: isRTL ? 'right' : 'left' }}>
              {getText(
                'Verification code sent. Please check your phone or email.',
                'تم إرسال رمز التفعيل. يرجى التحقق من هاتفك أو بريدك الإلكتروني.'
              )}
            </Alert>
          )}

          {showEmailVerification && (
            <Alert severity="warning" sx={{ mb: 2, textAlign: isRTL ? 'right' : 'left' }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {getText(
                  'Your account is not verified. Please check your phone or email for the verification code.',
                  'حسابك غير مفعّل. يرجى التحقق من هاتفك أو بريدك للحصول على رمز التفعيل.'
                )}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleResendOtp}
                disabled={resendLoading}
                sx={{ mt: 0.5 }}
              >
                {resendLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  getText('Resend code', 'إعادة إرسال الرمز')
                )}
              </Button>
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              label={getText('Email or phone', 'البريد الإلكتروني أو الهاتف')}
              name="identifier"
              type="text"
              value={identifier}
              onChange={(e) => {
                setIdentifier(e.target.value);
                setIdentifierError(validateIdentifier(e.target.value));
              }}
              margin="normal"
              required
              autoComplete="username"
              autoFocus
              error={!!identifierError}
              helperText={
                identifierError ||
                getText('Use email or Libyan mobile number', 'استخدم البريد أو رقم الهاتف الليبي')
              }
              placeholder={getText('email@domain.com or 09xxxxxxxx', 'email@domain.com أو 09xxxxxxxx')}
              inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
            />

            <TextField
              fullWidth
              label={getText('Password', 'كلمة المرور')}
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="current-password"
              inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={getText('Toggle password visibility', 'إظهار أو إخفاء كلمة المرور')}
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 1,
                py: 1.25,
                bgcolor: '#FF6500',
                fontWeight: 700,
                '&:hover': { bgcolor: '#e55a00' },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: '#fff' }} />
              ) : (
                getText('Sign In', 'تسجيل الدخول')
              )}
            </Button>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 2, textAlign: 'center' }}
          >
            {getText(
              'Access is for authorized staff only. Accounts are created by an administrator.',
              'الدخول مخصص للموظفين المصرّح لهم فقط. يتم إنشاء الحسابات بواسطة المسؤول.'
            )}
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
